import {
  BKJButton,
  BKJCheckbox,
  BKJIcon,
  BKJTextArea,
  BKJToggleInput,
} from '@bookjane2/bookjane-design-library';
import { useTranslation } from 'hooks/useTranslation';
import { BKJSideModalFooter } from 'components/BKJSideModalFooter';
import { Flex } from 'components/Flex';
import { ShiftDateCalendarInput } from 'components/ShiftDateCalendarInput';
import { ShiftModalComponentMap } from 'modals/ShiftModal/ShiftModal.constants';
import { SectionDivider } from 'modals/ShiftModal/ShiftModal.styled';
import {
  shiftModalRoutingService,
  useShiftModalUIContext,
} from 'modals/ShiftModal/ShiftModal.ui.context';
import { useShiftModalDataContext } from 'modals/ShiftModal/ShiftModalProvider.data.context';
import { ShiftCustomFieldInput } from 'modals/ShiftModal/src/components/ShiftCustomFieldInput/ShiftCustomFieldInput';
import { NotifyTeamMember } from 'modals/ShiftModal/src/views';
import {
  ShiftStatusDot,
  ShiftStatusText,
} from 'modals/ShiftModal/src/views/DetailView/DetailView.styled';
import {
  EditViewProvider,
  useEditViewContext,
} from 'modals/ShiftModal/src/views/EditView/EditView.context';
import {
  CustomFieldsFormLabel,
  FormLabel,
  SelectTeamMembersDropdownContainer,
  ValueText,
  WarningText,
} from 'modals/ShiftModal/src/views/EditView/EditView.styled';
import { ShiftTimeSelectInput } from 'components/ShiftTimeSelectInput';
import { ShiftUnpaidBreakTimeSelectInput } from 'components/ShiftUnpaidBreakTimeSelectInput';
import { H3 } from 'components/Typography';
import { FeatureGuard } from 'guards/FeatureGuard';
import { FEATURE_SELECTORS_MAP } from 'guards/FeatureGuard.constants';
import { MobileLabel } from 'pages/CreateShiftsPage/BulkCreateShiftView/CreateShiftModal/src/views/DetailsView/DetailsView.styled';
import { FC, Fragment, MouseEventHandler, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { CustomField, OpenShiftPolicyEnum, OrderStatusEnum } from 'types/graphql';
import { withMemo } from 'utils/withMemo';
import { getTranslatedResponse } from 'utils/getTranslatedResponse';
import schedulingReadAccessFacilityIdsSelector from 'store/selectors/scheduleSelectors/schedulingReadAccessFacilityIdsSelector';
import {
  ConfirmEditsView,
  PublishEditsWarningView,
} from 'modals/ShiftModal/src/views/ConfirmEditsView';
import { ReasonForEdits } from 'modals/ShiftModal/src/views/ReasonForEdits';
import { useShiftModalPropsContext } from 'modals/ShiftModal/ShiftModal.props.context';
import { AutoMargin } from 'components/AutoMargin';
import { DateTime } from 'luxon';
import { EditingReasonAndNotifyView } from 'modals/ShiftModal/src/views/DetailView/GeneralTab/ReasonForEditingAndNotify';
import { useAutoExtendBid } from 'hooks/useAutoExtendBid';
import { GroupedCustomersDropdown } from 'components/GroupedCustomersDropdown';
import { PositionByDepartmentSelectInput } from 'components/PositionByDepartmentSelectInput';
import { UseFormBehaviorsEventType } from 'hooks/useFormBehaviors/useFormBehaviors.types';
import {
  buildPositionChangeEvents,
  buildTeamMemberChangeEvents,
  getBiddingOvertimeDefault,
} from 'utils/shiftFormEvents';
import {
  isClockInClockOutFeatureEnabledSelector,
  isShiftBiddingFeatureEnabledSelector,
} from 'store/selectors/featureFlagSelectors';
import { getCurrentlySelectedLocation } from 'utils/getCurrentlySelectedLocation';
import {
  FetchPositionDetailsDataProvider,
  useFetchPositionDetailsDataContext,
} from 'modals/ShiftModal/src/views/EditView/FetchPositionDetails.context';
import { RequestTeamMemberForOrder } from 'components/RequestTeamMemberForOrder';
import { SkillsSelectInput, useCommunitySkills } from 'components/SkillsSelectInput';
import { useCanManageSkillsSelector } from 'store/selectors/skillsSelectors/canManageSkillsSelector';
import isUserEssentialSelector from 'store/selectors/userSelectors/isUserEssentialSelector';

export const shiftModalEditViewFormItemVerticalMargin = 24;

export const EditViewComponent: FC = withMemo(() => {
  const { t, i18n } = useTranslation();
  const { data } = useShiftModalDataContext();
  const order = data?.order;
  const { goToConfirmEditsView } = useShiftModalPropsContext();

  const isEssentialUser = useSelector(isUserEssentialSelector);

  const communityCustomFields = order.availableCustomFields || [];

  const isCustomFieldsEnabled =
    useSelector(FEATURE_SELECTORS_MAP['FEATURE_TYPE_CUSTOM_FIELDS']) &&
    !order!.isExternal &&
    communityCustomFields.length > 0;

  const {
    isDisabled,
    isEditInProgressShift,
    isEditPostShift,
    isSubmitting,
    onChange,
    isNotifyFlowRequired,
    totalDuration,
    totalPaidDuration,
    values,
    fieldErrors,
    hasCustomers,
    customers,
    currentOpenShiftPolicy,
    setCurrentOpenShiftPolicy,
  } = useEditViewContext();

  const isReassign = useMemo(() => {
    const currentPositionId = order.position.displayId;
    const currentJaneId = order.mainJane?.id || '';

    return currentPositionId !== values.positionId[0] || currentJaneId !== values.janeId;
  }, [order!.position?.displayId, order!.mainJane?.id, values?.positionId, values?.janeId]);
  const { value: orderStatusValue } = order.status;

  const biddingOvertimeDefault = getBiddingOvertimeDefault(order.community.biddingConfiguration);

  const selectedLocation = getCurrentlySelectedLocation();

  const locationsWithScheduling = useSelector(schedulingReadAccessFacilityIdsSelector);
  const isSchedulingEnabled = useMemo(
    () => locationsWithScheduling.includes(Number(order.community.id)),
    [order.community.id, locationsWithScheduling],
  );

  const isShiftBiddingFeatureEnabled = useSelector(isShiftBiddingFeatureEnabledSelector);

  const isClockInClockOutEnabled = useSelector(
    isClockInClockOutFeatureEnabledSelector(Number(order.community.displayId)),
  );

  const goToDeleteView = useCallback(() => {
    shiftModalRoutingService.goToView('DeleteView');
  }, []);

  const GetEditingReasonAndNotifyFlow = useCallback(() => {
    if (isReassign) {
      return goToConfirmEditsView();
    }

    switch (orderStatusValue) {
      case OrderStatusEnum.assigned:
      case OrderStatusEnum.accepted:
      case OrderStatusEnum.swapped:
        return isNotifyFlowRequired
          ? shiftModalRoutingService.goToView('EditingReasonAndNotifyView')
          : goToConfirmEditsView();
      case OrderStatusEnum.in_progress:
      case OrderStatusEnum.completed:
        return shiftModalRoutingService.goToView('EditingReasonAndNotifyView');
      default:
        return goToConfirmEditsView();
    }
  }, [isReassign, orderStatusValue, goToConfirmEditsView, isNotifyFlowRequired]);

  const handleSubmit = useCallback(
    (event: MouseEventHandler<HTMLButtonElement> & { preventDefault: () => void }) => {
      event.preventDefault();
      return GetEditingReasonAndNotifyFlow();
    },
    [GetEditingReasonAndNotifyFlow],
  );

  const allowShiftEditOneDayFuturePastFromStartDate =
    isEditInProgressShift || isEditPostShift
      ? {
          disabledFrom: DateTime.fromISO(order!.startDate).plus({ day: 1 }).toString(),
          disabledTo: DateTime.fromISO(order!.startDate).minus({ day: 1 }).toString(),
        }
      : undefined;

  const { shouldDisableAutoExtendBidToggle } = useAutoExtendBid({
    biddable: !!values.biddable,
    autoRebid: !!values.auto_rebid,
    sentToAgencies: !!values.sent_to_agencies,
    bids: Array.isArray(values.bids) ? (values.bids as string[]) : [],
    status: values.status as OrderStatusEnum,
    biddingTimeEndsAt: values.bidding_time_ends_at as string | undefined,
    timeZone: values.time_zone?.toString(),
  });

  const isAutoRebidEnabled = Boolean(order!.autoRebid && shouldDisableAutoExtendBidToggle);

  const hideReAssignDropdowns =
    // essential user
    isEssentialUser ||
    // biddable shift
    (isShiftBiddingFeatureEnabled && order!.biddable) ||
    // sent to agencies
    order!.sentToAgencies ||
    // in progress, completed shifts or unfulfilled
    orderStatusValue === OrderStatusEnum.in_progress ||
    orderStatusValue === OrderStatusEnum.completed ||
    orderStatusValue === OrderStatusEnum.unfulfilled;

  const { fetchPositionDetails } = useFetchPositionDetailsDataContext();

  const handlePositionChange = useCallback(
    async (e: UseFormBehaviorsEventType) => {
      const value = (e?.target?.value as string[]) || [];
      const label = (e?.target as UseFormBehaviorsEventType['target'] & { label?: string })?.label;
      let events = [e];

      if (value && value.length > 0) {
        try {
          const newPositionId = Number(value[0]);
          const openShiftPolicy = await fetchPositionDetails(newPositionId);

          setCurrentOpenShiftPolicy(openShiftPolicy);

          events = buildPositionChangeEvents({
            e,
            openShiftPolicy,
            isShiftBiddingFeatureEnabled,
            allowOvertimeFormFieldName: 'allow_overtime',
            autoBidFlagFormFieldName: 'auto_extend_bid',
            biddingOvertimeDefault,
            resets: [
              { name: 'byMultipleFields', value: '' },
              { name: 'teamMemberName', value: '' },
              { name: 'janeId', value: '' },
            ],
          });
        } catch (error) {}

        if (label) {
          events.push({ target: { name: 'positionName', value: label } });
        }
      } else {
        events.push({ target: { name: 'positionName', value: '' } });
      }

      onChange(events);
    },
    [
      onChange,
      fetchPositionDetails,
      order!.position?.displayId,
      order!.mainJane?.id,
      order!.allowOvertime,
      isShiftBiddingFeatureEnabled,
      biddingOvertimeDefault,
    ],
  );

  const handleTeamMemberChange = useCallback(
    (e: UseFormBehaviorsEventType) => {
      const isPositionChanged = Number(values.positionId?.[0]) !== order!.position?.displayId;
      const initialTeamMemberId = order!.mainJane?.relayId || '';
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const isTeamMemberChanged = ((e?.target as any)?.janeId || '') !== initialTeamMemberId;

      const events = buildTeamMemberChangeEvents({
        e,
        openShiftPolicy: currentOpenShiftPolicy,
        isShiftBiddingFeatureEnabled,
        allowOvertimeFormFieldName: 'allow_overtime',
        autoBidFlagFormFieldName: 'auto_extend_bid',
        initialAllowOvertime: order!.allowOvertime,
        biddingOvertimeDefault,
        isPositionChanged,
        isTeamMemberChanged,
        preserveAllowOvertimeOnAssign: true,
      });

      // Add additional fields for this specific view
      if (e?.target?.value) {
        if (e?.target?.label) {
          events.push({ target: { name: 'teamMemberName', value: e.target.label } });
        }
        if ((e?.target as UseFormBehaviorsEventType['target'] & { janeId?: string })?.janeId) {
          events.push({
            target: {
              name: 'janeId',
              value: (e.target as UseFormBehaviorsEventType['target'] & { janeId?: string })
                .janeId!,
            },
          });
        }
      } else {
        events.push({ target: { name: 'teamMemberName', value: '' } });
        events.push({ target: { name: 'janeId', value: '' } });
      }

      onChange(events);
    },
    [
      values.positionId,
      order!.position?.displayId,
      order!.mainJane?.relayId,
      order!.mainJane?.id,
      order!.allowOvertime,
      currentOpenShiftPolicy,
      isShiftBiddingFeatureEnabled,
      biddingOvertimeDefault,
      order,
      onChange,
    ],
  );

  const isCurrentPositionBidding =
    isShiftBiddingFeatureEnabled && currentOpenShiftPolicy === OpenShiftPolicyEnum.bidding_required;

  const isJaneNotReassigned =
    values.janeId === order.mainJane?.id || (!values.janeId && !order.mainJane?.id);

  const isAllowOvertimeEnabled =
    !order!.recurrence &&
    isJaneNotReassigned &&
    orderStatusValue === OrderStatusEnum.pending &&
    isSchedulingEnabled &&
    !isCurrentPositionBidding;

  const isTeamMemberAssigned = !!values.janeId;

  const canManageSkills = useCanManageSkillsSelector();
  const isSkillsSelectionEditable =
    canManageSkills &&
    !order!.biddable &&
    !isTeamMemberAssigned &&
    !isEditInProgressShift &&
    !isEditPostShift;
  const isSkillsSelectionVisible =
    isSkillsSelectionEditable || (canManageSkills && !!order!.ordersSkills?.length);
  const { options: communitySkillOptions } = useCommunitySkills(
    isSkillsSelectionEditable ? Number(order.community.displayId) : undefined,
  );
  const skillOptions = useMemo(() => {
    if (isSkillsSelectionEditable) return communitySkillOptions;
    return (order!.ordersSkills ?? []).map(({ skill }) => ({
      id: `${skill.displayId}`,
      key: `${skill.displayId}`,
      value: `${skill.displayId}`,
      label: skill.name,
      name: skill.name,
    }));
  }, [isSkillsSelectionEditable, communitySkillOptions, order]);

  return (
    <Fragment>
      <Flex flexDirection="column" padding="32px" flex="1">
        <H3 color="TextDarkGrey">{t('shift_modal:EDIT_SHIFT')}</H3>

        <Flex padding="24px 0" alignItems="center">
          <ShiftStatusDot
            type={order!.status.name.toLowerCase()}
            isBlockShift={order!.isBlockShift}
          >
            •
          </ShiftStatusDot>
          <ShiftStatusText>
            {getTranslatedResponse(order!.status.name, 'status', i18n, t)}
          </ShiftStatusText>
        </Flex>

        <Flex
          flexDirection="column"
          width="100%"
          margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
          gap="0px 12px"
        >
          <FormLabel>{t('shift_modal:POSITION')}</FormLabel>
          {hideReAssignDropdowns ? (
            <ValueText>{order!.position.name}</ValueText>
          ) : (
            <PositionByDepartmentSelectInput
              name="positionId"
              type="Single"
              value={values.positionId.map((id) => String(id))}
              onChange={handlePositionChange}
              location={selectedLocation}
              variant="GreyOutlined"
              width="100%"
              dropdownWidth="480px"
              placeholder={t('shift_modal:SELECT_POSITION')}
              dropdownProps={{
                dropdownListItemVariant: 'List',
              }}
              iconProps={{ iconName: 'Search' }}
            />
          )}
        </Flex>

        {hasCustomers && (
          <Flex
            width="100%"
            margin={`${shiftModalEditViewFormItemVerticalMargin}px 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
            flexDirection="column"
          >
            <FormLabel>{t('common:CUSTOMER')}</FormLabel>
            <GroupedCustomersDropdown
              variant="single"
              name="customer"
              value={values.customer || undefined}
              onChange={onChange}
              customersData={customers}
              error={fieldErrors?.customer ? (t('errors:CUSTOMER_REQUIRED') as string) : undefined}
            />
          </Flex>
        )}

        {!hideReAssignDropdowns && (
          <Flex
            flexDirection="column"
            width="100%"
            margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
          >
            <FormLabel>{t('shift_modal:TEAM_MEMBER')} (Optional)</FormLabel>
            <SelectTeamMembersDropdownContainer>
              <RequestTeamMemberForOrder
                key={JSON.stringify([
                  values.positionId,
                  values?.start_date?.[0],
                  values?.start_time?.[0],
                  values?.end_time?.[0],
                  values?.unpaid_break_time?.[0],
                  values?.allow_overtime,
                ])}
                name="byMultipleFields"
                value={values.byMultipleFields}
                onChange={handleTeamMemberChange}
                location={selectedLocation}
                byPosition={values.positionId}
                startDate={values?.start_date?.[0]}
                startAt={values?.start_time?.[0]}
                endAt={values?.end_time?.[0]}
                breakTime={values.unpaid_break_time?.[0]}
                allowOvertime={values.allow_overtime}
                orderId={order!.relayId}
                variant="modal"
                width="100%"
                css={{
                  width: '100%',
                }}
              />
            </SelectTeamMembersDropdownContainer>
          </Flex>
        )}

        <Flex
          flexDirection="column"
          width="100%"
          margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
          gap="0px 12px"
        >
          <Flex flexDirection="column" width="50%" padding="0 8px 0 0">
            <FormLabel>{t('shift_modal:DATE')}</FormLabel>
            <ShiftDateCalendarInput
              name="start_date"
              iconComponent={BKJIcon}
              iconProps={{
                iconName: 'ShiftsDate',
              }}
              value={values.start_date}
              onChange={onChange}
              disabled={values.zeroHoursWorked}
              dropdownProps={allowShiftEditOneDayFuturePastFromStartDate}
            />
          </Flex>
        </Flex>

        <Flex width="100%" margin="0 0 24px">
          <Flex flexDirection="column" width="50%" padding="0 8px 0 0">
            <ShiftTimeSelectInput
              label={t('shift_modal:START_TIME')}
              placeholder={t('shift_modal:START_TIME')}
              onChange={onChange}
              name="start_time"
              value={values.start_time}
              error={fieldErrors?.start_time}
              disabled={values.zeroHoursWorked}
            />
          </Flex>

          <Flex flexDirection="column" width="50%" padding="0 0 0 8px">
            <ShiftTimeSelectInput
              label={t('shift_modal:END_TIME')}
              placeholder={t('shift_modal:END_TIME')}
              onChange={onChange}
              name="end_time"
              value={values.end_time}
              error={fieldErrors?.end_time}
              disabled={values.zeroHoursWorked}
            />
          </Flex>
        </Flex>

        <Flex width="100%" margin="0 0 24px">
          <Flex flexDirection="column" width="50%" padding="0 8px 0 0">
            <ShiftUnpaidBreakTimeSelectInput
              label={t('shift_modal:UNPAID_BREAK')}
              width="100%"
              placeholder={t('shift_modal:UNPAID_BREAK')}
              onChange={onChange}
              name="unpaid_break_time"
              value={values.unpaid_break_time}
              error={fieldErrors?.unpaid_break_time}
              disabled={values.zeroHoursWorked}
            />
          </Flex>
        </Flex>

        <Flex width="100%" margin="0 0 24px">
          <Flex flexDirection="column" width="50%" padding="0 8px 0 0">
            <FormLabel>{t('shift_modal:TOTAL_DURATION')}</FormLabel>
            <ValueText>{totalDuration}</ValueText>
          </Flex>

          <Flex flexDirection="column" width="50%" padding="0 0 0 8px">
            <FormLabel>{t('shift_modal:TOTAL_PAID_DURATION')}</FormLabel>
            <ValueText
              isError={
                !!fieldErrors?.start_time ||
                !!fieldErrors?.end_time ||
                !!fieldErrors?.unpaid_break_time
              }
            >
              {values.zeroHoursWorked ? '0 hrs' : totalPaidDuration}
            </ValueText>
          </Flex>
        </Flex>
        {isSkillsSelectionVisible && (
          <Flex
            flexDirection="column"
            width="100%"
            margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
          >
            <CustomFieldsFormLabel>{t('create_shifts:CALL_OUT_CRITERIA')}</CustomFieldsFormLabel>
            <SkillsSelectInput
              name="required_skills"
              value={values.required_skills}
              onChange={onChange}
              options={skillOptions}
              label={t('create_shifts:SKILLS')}
              placeholder={t('create_shifts:SELECT_SKILLS')}
              max={skillOptions.length || undefined}
              disabled={!isSkillsSelectionEditable}
            />
          </Flex>
        )}
        {(isEditInProgressShift || isEditPostShift) && !isClockInClockOutEnabled && (
          <Flex flexDirection="column" width="100%" margin="0 0 24px">
            <Flex alignItems="center">
              <Flex flexDirection="column" padding="0 8px 0 0">
                <BKJCheckbox
                  variant={'GreenSolid'}
                  name="zeroHoursWorked"
                  value={values.zeroHoursWorked}
                  onChange={onChange}
                />
              </Flex>
              <Flex flexDirection="column" justifyContent="center">
                <ValueText>{t('shift_modal:ABSENT')}</ValueText>
              </Flex>
            </Flex>
            <WarningText marginTop={8}>{t('shift_modal:ABSENT_WARNING')}</WarningText>
          </Flex>
        )}
        {!order.isExternal && (
          <FeatureGuard type="FEATURE_TYPE_SCHEDULING">
            <Flex
              margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
              alignItems="center"
              gap="16px"
            >
              <ValueText>{t('shift_modal:ALLOW_OVERTIME')}</ValueText>
              <BKJToggleInput
                variant="Switch"
                name="allow_overtime"
                disabled={!isAllowOvertimeEnabled}
                onChange={onChange}
                checked={values.allow_overtime}
              />
            </Flex>
          </FeatureGuard>
        )}
        {isCurrentPositionBidding && !isTeamMemberAssigned && (
          <Flex
            margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
            alignItems="center"
            gap="16px"
          >
            <ValueText>{t('create_shifts:AUTO_EXTEND_BID')}</ValueText>
            <BKJToggleInput
              variant="Switch"
              name="auto_extend_bid"
              onChange={onChange}
              disabled={isAutoRebidEnabled}
              checked={values.auto_extend_bid}
            />
          </Flex>
        )}

        <Flex flexDirection="column">
          <FormLabel>{t('shift_modal:SHIFT_NOTES')}</FormLabel>
          <BKJTextArea
            name="description"
            placeholder={t('shift_modal:TYPE_NOTES')}
            value={values.description}
            onChange={onChange}
            maxLength={2000}
          />
        </Flex>
        {isCustomFieldsEnabled && (
          <Fragment>
            <SectionDivider
              variant="Thin"
              margin={`${shiftModalEditViewFormItemVerticalMargin}px 0`}
            />
            {communityCustomFields?.map((customField) => {
              return (
                <Flex
                  key={customField.id}
                  flexDirection="column"
                  margin={`0 0 ${shiftModalEditViewFormItemVerticalMargin}px 0`}
                >
                  <FormLabel>
                    {customField.name}
                    {customField.mobile && (
                      <MobileLabel>
                        &nbsp;({t('shift_modal:SHOWN_INTERNALLY_ON_MOBILE')})
                      </MobileLabel>
                    )}
                  </FormLabel>
                  <ShiftCustomFieldInput
                    key={customField.id}
                    customField={customField as CustomField}
                    value={values[customField.name] as string}
                    onChange={onChange}
                  />
                </Flex>
              );
            })}
          </Fragment>
        )}
      </Flex>

      <BKJSideModalFooter justifyContent="space-between">
        {!!order.isDeletable && (
          <BKJButton width="auto" variant="RedText" onClick={goToDeleteView}>
            {t('shift_modal:DELETE')}
          </BKJButton>
        )}
        <AutoMargin />
        <BKJButton
          variant="PurpleSolid"
          width="180px"
          disabled={isDisabled}
          isLoading={isSubmitting}
          type="button"
          onClick={handleSubmit as unknown as MouseEventHandler<HTMLButtonElement>}
        >
          {t('shift_modal:NEXT')}
        </BKJButton>
      </BKJSideModalFooter>
    </Fragment>
  );
});

export const EditView: FC = () => {
  return (
    <FetchPositionDetailsDataProvider>
      <EditViewComponent />
    </FetchPositionDetailsDataProvider>
  );
};

export const EditViewRenderer: FC = withMemo((props) => {
  const { shiftModalActiveModalView } = useShiftModalUIContext();
  const Component = {
    ...ShiftModalComponentMap,
    EditView,
    NotifyTeamMember,
    ReasonForEdits,
    ConfirmEditsView,
    PublishEditsWarningView,
    EditingReasonAndNotifyView,
  }[shiftModalActiveModalView];
  return (
    <EditViewProvider>
      <Component {...props} />
    </EditViewProvider>
  );
});
